import type {
  PlatformPlanChangePreview,
  PlatformPlanDetail,
  PlatformSubscriptionAdministrationState,
  PlatformSubscriptionMutationResponse
} from "./platform-types";

function apiError(response: Response, payload: unknown): Error {
  const problem = payload as { detail?: string; title?: string };
  const error = new Error(problem.detail ?? problem.title ?? "Platform administration request failed.");
  Object.assign(error, { status: response.status, problem: payload });
  return error;
}

async function adminFetch<T>(path: string, token: string, options: RequestInit = {}): Promise<T> {
  const response = await fetch(path, {
    ...options,
    headers: {
      Accept: "application/json",
      ...(options.body ? { "Content-Type": "application/json" } : {}),
      Authorization: `Bearer ${token}`,
      ...(options.headers ?? {})
    }
  });

  if (response.status === 204) return null as T;
  const payload = await response.json().catch(() => ({}));
  if (!response.ok) throw apiError(response, payload);
  return payload as T;
}

function mutationOptions(body: unknown, idempotencyKey: string): RequestInit {
  return {
    method: "POST",
    headers: { "Idempotency-Key": idempotencyKey },
    body: JSON.stringify(body)
  };
}

export function createPlatformIdempotencyKey(prefix: string): string {
  return `${prefix}:${crypto.randomUUID()}`;
}

export function getPlatformSubscriptionAdministrationState(
  token: string,
  tenantId: string
): Promise<PlatformSubscriptionAdministrationState> {
  return adminFetch<PlatformSubscriptionAdministrationState>(
    `/api/v1/platform-console/tenants/${encodeURIComponent(tenantId)}/subscription/admin-state`,
    token
  );
}

export function executePlatformSubscriptionCommand(
  token: string,
  tenantId: string,
  command: string,
  body: unknown,
  idempotencyKey: string
): Promise<PlatformSubscriptionMutationResponse> {
  return adminFetch<PlatformSubscriptionMutationResponse>(
    `/api/v1/platform-console/tenants/${encodeURIComponent(tenantId)}/subscription/${command}`,
    token,
    mutationOptions(body, idempotencyKey)
  );
}

export function previewPlatformPlanChange(
  token: string,
  tenantId: string,
  targetPlanVersionId: string
): Promise<PlatformPlanChangePreview> {
  return adminFetch<PlatformPlanChangePreview>(
    `/api/v1/platform-console/tenants/${encodeURIComponent(tenantId)}/subscription/plan-change-preview`,
    token,
    { method: "POST", body: JSON.stringify({ targetPlanVersionId }) }
  );
}

export function getPlatformPlanDetail(token: string, planId: string): Promise<PlatformPlanDetail> {
  return adminFetch<PlatformPlanDetail>(`/api/v1/platform-console/plan-admin/${encodeURIComponent(planId)}`, token);
}

export function executePlatformPlanCommand(
  token: string,
  path: string,
  method: "POST" | "PUT",
  body: unknown,
  idempotencyKey: string
): Promise<PlatformPlanDetail> {
  return adminFetch<PlatformPlanDetail>(
    `/api/v1/platform-console/plan-admin${path}`,
    token,
    {
      method,
      headers: { "Idempotency-Key": idempotencyKey },
      body: JSON.stringify(body)
    }
  );
}
